import * as React from 'react';
import { isMobile } from 'react-device-detect';
import { useLocation, useNavigate } from 'react-router-dom';

import Badge from '@/components/elements/Badge';
import SelectButton from '@/components/elements/Button/SelectButton';
import { RaceEntryStatus } from '@/lib/apollo/types/gen/graphql';
import { RaceInfoType } from '@/modelTypes';

import { SelectButtonWrapper } from './style';

interface RaceInfoTypeSelectorProps {
  isBettable: boolean;
  isCanceled: boolean;
  hasOrderTicket?: boolean;
  raceEntryStatus: RaceEntryStatus;
  raceInfoType: RaceInfoType;
}

interface ButtonType {
  name: string;
  type: RaceInfoType;
  displayDevice: 'mobile' | 'pc' | 'both';
  disabled?: boolean;
  isHidden?: boolean;
  showBadge?: boolean;
}

const RaceInfoTypeSelector: React.FC<RaceInfoTypeSelectorProps> = React.memo(
  ({
    isBettable,
    isCanceled,
    hasOrderTicket,
    raceEntryStatus,
    raceInfoType,
  }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const userDevice = isMobile ? 'mobile' : 'pc';

    const Buttons: ButtonType[] = [
      {
        name: '出走表',
        type: RaceInfoType.Basic,
        displayDevice: 'both',
      },
      {
        name: '直近成績',
        type: RaceInfoType.RecentResult,
        displayDevice: 'pc',
      },
      {
        name: '直近',
        type: RaceInfoType.RecentResult,
        displayDevice: 'mobile',
      },
      {
        name: '当場成績',
        type: RaceInfoType.VelodromeResult,
        displayDevice: 'pc',
      },
      {
        name: '当場',
        type: RaceInfoType.VelodromeResult,
        displayDevice: 'mobile',
      },
      {
        name: 'オッズ',
        type: RaceInfoType.Odds,
        displayDevice: 'pc',
        isHidden: isBettable,
        disabled:
          isCanceled ||
          [RaceEntryStatus.StopRace, RaceEntryStatus.StopHeld].includes(
            raceEntryStatus,
          ),
      },
      {
        name: '結果',
        type: RaceInfoType.Result,
        displayDevice: 'both',
        disabled: raceEntryStatus !== RaceEntryStatus.Decision,
      },
      {
        name: '購入車券',
        type: RaceInfoType.Order,
        displayDevice: 'pc',
        showBadge: hasOrderTicket,
      },
    ];

    const displayItems = Buttons.filter(
      btn => [userDevice, 'both'].includes(btn.displayDevice) && !btn.isHidden,
    );

    const handleClick = (type: RaceInfoType) => {
      navigate(`${location.pathname}?infoType=${type}`, {
        state: { cancelScroll: true },
      });
    };

    return (
      <SelectButtonWrapper>
        {displayItems.map((item, i) => (
          <Badge
            key={`${item.name}-${i}`}
            isShow={Boolean(item.showBadge)}
            top="-5px"
            right="-5px"
          >
            <SelectButton
              bgColor="secondary"
              size="s"
              width="100%"
              height="46px"
              clicked={raceInfoType === item.type}
              disabled={item.disabled}
              outline
              onClick={() => handleClick(item.type)}
            >
              {item.name}
            </SelectButton>
          </Badge>
        ))}
      </SelectButtonWrapper>
    );
  },
);

export default RaceInfoTypeSelector;
